<script lang="ts" module>
	import Render from '#components/render.svelte'
	import { Storyblok } from '#library/storyblok.svelte.ts'
	import type { Event } from '#library/types.ts'
	import Header from '#blocks/header.svelte'
	import Hero from '#blocks/hero.svelte'
	import Date from '#components/date.svelte'
	import Share from '#components/share.svelte'
	import Latest from '#blocks/latest.svelte'
	import * as _ from 'radashi'
	import type { StoryblokAsset, Event as EventStory } from '#library/components.schema.build.ts'
	import type { ISbStories } from '@storyblok/svelte'

	// results go on the story, not module scope: module state is shared by every
	// request the server process handles, so one visitor's related events were
	// served to everyone else. `story` is per-request, so writing there is safe.
	export const load: Load<Event> = async ({ story, api, version, event: { url } }) => {
		if (story.content.related_events?.length) {
			const events = story.content.related_events.filter(e => typeof e !== 'string')

			// return so we don't get the other events
			story.content.more = {
				title: 'Related events',
				events
			}

			return
		}

		/** Fallback to retrieve 3 other stories (with event type) */
		const {
			data: { stories: events }
		} = (await api.get('cdn/stories', {
			version,
			content_type: 'event',
			per_page: 3,
			resolve_links: 'url',
			excluding_slugs: url.pathname.substring(1)
		})) as ISbStories<EventStory>

		story.content.more = {
			title: 'Other events',
			events
		}
	}
</script>

<script lang="ts">
	interface Props {
		data: Storyblok<Event>
	}

	let { data }: Props = $props()

	const content = $derived(data.story?.content)
</script>

{#if data.story && content}
	<Header image={content?.portrait ? content.image : undefined} />

	{#if content.image?.filename && !content?.portrait}
		<Hero blok={{ image: content.image as StoryblokAsset }} />
	{/if}

	<div class="contain event" class:portrait={content?.portrait}>
		<div class="content">
			<div class="subtitle">{content.headline}</div>
		</div>

		{#if content.dates?.length}
			{#each content.dates as date, index (index)}
				<Date
					theme="theme-crocus-on-white"
					dates={[date]}
					price={date.price}
					event={date.venue}
					href={date.link?.url ?? undefined}
				/>
			{/each}
		{/if}
		<div class="content">
			<div>
				{#if content.body}
					<Render content={content.body} />
				{/if}
			</div>
		</div>
	</div>

	{#if data.story.content.blocks}
		{#each data.story.content.blocks as blok (blok._uid)}
			<Storyblok.Block blok={{ ...blok, padding_top: '0', padding_bottom: '0' }} />
		{/each}
	{/if}

	<div class="share">
		<Share />
	</div>

	{#if content.more}
		<Latest
			blok={{
				types: ['events'],
				events: _.cluster(content.more.events, 3),
				title: content.more.title
			}}
		/>
	{/if}
{/if}

<style lang="css">
	.event {
		padding-block: var(--s-40);

		&.portrait {
			padding-block: 0 var(--s-40);
		}
	}

	.content {
		display: grid;
		margin-block: var(--s-40);

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}
	}

	.subtitle {
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.share {
		margin-block-end: var(--s-96);
	}
</style>
